import { AlertTriangle, Brain, CheckCircle, Info } from 'lucide-react';
import { useDealAnalysis } from '@/hooks/use-deal-analysis';
import { useSecurities, useSecuritySummary } from '@/hooks/use-securities';
import type { Loan } from '@/types';

interface Props {
  loan: Loan;
}

/**
 * AI Insights Panel — generates risk commentary and loan insights.
 *
 * Phase 1 (current): Rule-based risk scoring with structured analysis.
 * Phase 2 (future): Integrate with Claude API for natural language risk commentary,
 *   help chatbot, and intelligent loan structuring suggestions.
 *
 * Architecture for LLM integration:
 * - Backend: POST /loans/:id/ai-insights — sends loan data to Claude API
 * - Backend: POST /ai/chat — conversational help endpoint with loan context
 * - Frontend: This component + a chat drawer component
 * - API Key stored in env (ANTHROPIC_API_KEY)
 * - Context includes: loan terms, borrower profile, security LTV, fee structure,
 *   comparable market data, repayment history, credit decisions
 */
export function AiInsightsPanel({ loan }: Props) {
  const { data: dealAnalysis } = useDealAnalysis(loan.id);
  const { data: securities } = useSecurities(loan.id);
  const { data: securitySummary } = useSecuritySummary(loan.id);

  // ── Rule-based risk scoring ──────────────────────────

  const riskFactors: {
    level: 'high' | 'medium' | 'low' | 'info';
    label: string;
    detail: string;
  }[] = [];

  // LTV risk
  if (securitySummary) {
    if (securitySummary.totalLtv > 80) {
      riskFactors.push({
        level: 'high',
        label: 'High LTV',
        detail: `Total LTV is ${securitySummary.totalLtv.toFixed(1)}% — above 80% threshold. Consider additional security or reduced principal.`,
      });
    } else if (securitySummary.totalLtv > 65) {
      riskFactors.push({
        level: 'medium',
        label: 'Moderate LTV',
        detail: `Total LTV is ${securitySummary.totalLtv.toFixed(1)}% — within acceptable range but monitor.`,
      });
    } else if (securitySummary.totalLtv > 0) {
      riskFactors.push({
        level: 'low',
        label: 'Conservative LTV',
        detail: `Total LTV is ${securitySummary.totalLtv.toFixed(1)}% — strong security position.`,
      });
    }

    if (securitySummary.securityCover < 1.2) {
      riskFactors.push({
        level: 'high',
        label: 'Low Security Cover',
        detail: `Security cover is ${securitySummary.securityCover.toFixed(2)}x — below 1.2x minimum. Net equity may not cover the loan.`,
      });
    }
  }

  // No security items
  if (!securities || securities.length === 0) {
    riskFactors.push({
      level: 'high',
      label: 'Unsecured',
      detail: 'No security items registered against this loan.',
    });
  }

  // Prior encumbrances
  const hasEncumbrances = securities?.some(s => s.priorEncumbrances > 0);
  if (hasEncumbrances) {
    riskFactors.push({
      level: 'medium',
      label: 'Prior Encumbrances',
      detail: 'One or more security items have existing debt ranking ahead of this loan.',
    });
  }

  // Term risk
  if (loan.termMonths > 24) {
    riskFactors.push({
      level: 'medium',
      label: 'Long Term',
      detail: `${loan.termMonths} month term exceeds 24 months — increased exposure duration.`,
    });
  }

  // Multiple extensions risk
  const extensionCount = loan.extensions?.length ?? 0;
  if (extensionCount >= 3) {
    riskFactors.push({
      level: 'high',
      label: 'Multiple Extensions',
      detail: `Loan has been extended ${extensionCount} times — may indicate borrower difficulty in meeting original terms.`,
    });
  } else if (extensionCount >= 2) {
    riskFactors.push({
      level: 'medium',
      label: 'Repeated Extensions',
      detail: `Loan has been extended ${extensionCount} times — monitor borrower's ability to repay.`,
    });
  } else if (extensionCount === 1) {
    riskFactors.push({
      level: 'info',
      label: 'Term Extended',
      detail: `Loan term extended once (+${loan.extensions![0].additionalMonths} months).`,
    });
  }

  // Interest rate
  const rate = parseFloat(loan.interestRate);
  if (rate < 10) {
    riskFactors.push({
      level: 'info',
      label: 'Below-Market Rate',
      detail: `Interest rate of ${rate.toFixed(2)}% may be below private lending benchmarks.`,
    });
  }

  // Deal return
  if (dealAnalysis) {
    if (dealAnalysis.annualisedReturn < 8) {
      riskFactors.push({
        level: 'high',
        label: 'Low Annualised Return',
        detail: `${dealAnalysis.annualisedReturn.toFixed(2)}% annualised return is below 8% target threshold.`,
      });
    } else if (dealAnalysis.annualisedReturn >= 15) {
      riskFactors.push({
        level: 'low',
        label: 'Strong Return',
        detail: `${dealAnalysis.annualisedReturn.toFixed(2)}% annualised return exceeds target.`,
      });
    }

    if (dealAnalysis.totalPaidOut > dealAnalysis.totalLenderRetainedFees) {
      riskFactors.push({
        level: 'medium',
        label: 'High Referrer Payout',
        detail: `Referrer commission (${fmt(dealAnalysis.totalPaidOut)}) exceeds lender retained fees (${fmt(dealAnalysis.totalLenderRetainedFees)}).`,
      });
    }
  }

  // Default rate check
  if (!loan.defaultRate) {
    riskFactors.push({
      level: 'info',
      label: 'No Default Rate',
      detail: 'No default interest rate set. Consider adding a penalty rate for default scenarios.',
    });
  }

  // Delinquency status
  if (['DELINQUENT_30', 'DELINQUENT_60', 'DELINQUENT_90', 'DEFAULT'].includes(loan.status)) {
    riskFactors.push({
      level: 'high',
      label: 'In Arrears',
      detail: `Loan is in ${loan.status.replace('_', ' ').toLowerCase()} status. Immediate attention required.`,
    });
  }

  // Sort: high first, then medium, low, info
  const order = { high: 0, medium: 1, low: 2, info: 3 };
  riskFactors.sort((a, b) => order[a.level] - order[b.level]);

  const highCount = riskFactors.filter(r => r.level === 'high').length;
  const medCount = riskFactors.filter(r => r.level === 'medium').length;

  // Overall risk rating
  let overallRisk: 'HIGH' | 'MEDIUM' | 'LOW' = 'LOW';
  let riskColor = 'text-green-700 bg-green-50 border-green-200';
  if (highCount >= 2) {
    overallRisk = 'HIGH';
    riskColor = 'text-red-700 bg-red-50 border-red-200';
  } else if (highCount >= 1 || medCount >= 2) {
    overallRisk = 'MEDIUM';
    riskColor = 'text-amber-700 bg-amber-50 border-amber-200';
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-semibold text-gray-900">AI Risk Insights</h3>
          <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-medium">
            Phase 1 — Rule-based
          </span>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-bold border ${riskColor}`}>
          {overallRisk} RISK
        </div>
      </div>

      {/* Risk factors */}
      <div className="space-y-2">
        {riskFactors.map((factor, i) => (
          <RiskFactorCard key={i} factor={factor} />
        ))}
      </div>

      {riskFactors.length === 0 && (
        <div className="bg-green-50 rounded-lg border border-green-200 p-4 text-center">
          <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
          <p className="text-sm text-green-700 font-medium">No risk factors identified</p>
          <p className="text-xs text-green-600 mt-0.5">Loan appears well-structured</p>
        </div>
      )}

      {/* LLM Integration Roadmap */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">
          Coming in Phase 2 — LLM Integration
        </p>
        <div className="space-y-1.5 text-xs text-gray-400">
          <p>• Natural language risk commentary powered by Claude API</p>
          <p>• Conversational help chatbot with full loan context</p>
          <p>• Intelligent loan structuring suggestions</p>
          <p>• Automated credit committee summary drafting</p>
          <p>• Market comparison analysis from property data</p>
        </div>
      </div>
    </div>
  );
}

function RiskFactorCard({ factor }: { factor: { level: string; label: string; detail: string } }) {
  const styles = {
    high: {
      icon: AlertTriangle,
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-700',
      badge: 'bg-red-100 text-red-700',
    },
    medium: {
      icon: AlertTriangle,
      bg: 'bg-amber-50 border-amber-200',
      text: 'text-amber-700',
      badge: 'bg-amber-100 text-amber-700',
    },
    low: {
      icon: CheckCircle,
      bg: 'bg-green-50 border-green-200',
      text: 'text-green-700',
      badge: 'bg-green-100 text-green-700',
    },
    info: {
      icon: Info,
      bg: 'bg-blue-50 border-blue-200',
      text: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-700',
    },
  };

  const style = styles[factor.level as keyof typeof styles] || styles.info;
  const Icon = style.icon;

  return (
    <div className={`rounded-lg border p-3 ${style.bg}`}>
      <div className="flex items-start gap-2.5">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.text}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-xs font-semibold ${style.text}`}>{factor.label}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${style.badge}`}>
              {factor.level.toUpperCase()}
            </span>
          </div>
          <p className={`text-xs ${style.text} opacity-80`}>{factor.detail}</p>
        </div>
      </div>
    </div>
  );
}

function fmt(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
  }).format(value);
}
